import { Injectable, computed, inject, signal } from '@angular/core';
import { OnboardingService, MyApplicationSummary } from './onboarding/onboarding.service';

const STORE_KEY = 'console.activeEnterprise';

// EnterpriseStore：多企业切换中枢。持有当前用户名下企业列表 + 选中企业（application_id），
// 选中态持久化到 localStorage。各业务页（档案/API Keys）从此读「当前企业」做隔离。
@Injectable({ providedIn: 'root' })
export class EnterpriseStore {
  private onb = inject(OnboardingService);

  readonly enterprises = signal<MyApplicationSummary[]>([]);
  readonly activeId = signal<string>('');
  readonly loaded = signal(false);

  readonly active = computed<MyApplicationSummary | null>(() => {
    const id = this.activeId();
    return this.enterprises().find((e) => e.applicationId === id) ?? this.enterprises()[0] ?? null;
  });
  readonly hasMultiple = computed(() => this.enterprises().length > 1);

  // 加载企业列表；选中态优先取 localStorage，失效则回退首个。幂等（已加载不重复拉）。
  async load(force = false): Promise<void> {
    if (this.loaded() && !force) return;
    try {
      const reply = await this.onb.listMyApplications();
      const list = reply.applications ?? [];
      this.enterprises.set(list);
      const saved = this.read();
      const valid = list.find((e) => e.applicationId === saved);
      this.activeId.set(valid?.applicationId ?? list[0]?.applicationId ?? '');
      this.loaded.set(true);
    } catch {
      // 拉取失败：仅在【首次加载】时降级为空态；【强制刷新】（窗口 focus 重拉等）时
      // 保留已有的企业列表与选中态。否则一次瞬时失败（token 刷新窗口内的 401 / 网络抖动 /
      // 与其它请求的竞态）就会清空整个企业上下文，导致 X-Enterprise-Id 变空、
      // 随后所有 BFF 请求被拒（如虚拟卡页报 "Invalid request"）。已有好状态绝不因一次刷新失败而丢。
      if (!this.loaded()) {
        this.enterprises.set([]);
        this.activeId.set('');
        this.loaded.set(true);
      }
    }
  }

  setActive(applicationId: string): void {
    if (!applicationId || applicationId === this.activeId()) return;
    this.activeId.set(applicationId);
    this.write(applicationId);
  }

  private read(): string {
    if (typeof localStorage === 'undefined') return '';
    try {
      return localStorage.getItem(STORE_KEY) ?? '';
    } catch {
      return '';
    }
  }
  private write(id: string): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(STORE_KEY, id);
    } catch {
      /* 隐私模式等：忽略持久化失败 */
    }
  }
}
